import { selectedScreen } from './nativeScreens.js'
import { editableManifest } from './nativeSportsbookManifest'
import { effectiveSectionBindings } from './nativeSectionReuse'
import { inspectionFieldLabel } from './nativeInspectionFields'

export const NATIVE_HISTORY_LIMIT = 100

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical)
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]))
  return value
}

// Theme and publication stamps have separate owners. Saving with a newer theme
// must not turn an unchanged Home composition into an unsaved edit.
export function draftFingerprint(draft, bindings = []) {
  const content = { ...draft }
  for (const key of ['theme', 'version', 'issued_at']) delete content[key]
  if (bindings.length) content.section_bindings = bindings.map(({ component_id, section_id, version, identity_map }) => ({ component_id, section_id, version, identity_map }))
  return JSON.stringify(canonical(content))
}

export function initialEditorSelection(draft) {
  return { screenId: selectedScreen(draft)?.id, componentId: selectedScreen(draft).components[0]?.id ?? null, cardId: null, surface: 'home', templateId: null, sceneId: null, eventId: null, layer: 'base', section: 'event_header', phase: 'pre_match' }
}

function validSelection(selection, draft) {
  const screen = selectedScreen(draft, selection)
  const component = screen.components.find((item) => item.id === selection.componentId)
  const card = component?.cards?.find((item) => item.id === selection.cardId)
  const field = selection.surface === 'home' && inspectionFieldLabel(card, selection.field) ? selection.field : null
  const navigation = selection.componentId?.startsWith('navigation:') && screen.navigation?.[selection.componentId.split(':')[1]]
  return {
    ...selection,
    screenId: screen.id,
    field,
    fieldSelectionId: field ? selection.fieldSelectionId : null,
    componentId: component || navigation ? selection.componentId : screen.components[0]?.id ?? null,
    cardId: component?.cards?.some((item) => item.id === selection.cardId) ? selection.cardId : null,
    templateId: draft.experience?.event_templates.some((item) => item.id === selection.templateId) ? selection.templateId : null,
    sceneId: draft.experience?.home_scenes.some((item) => item.id === selection.sceneId) ? selection.sceneId : null,
  }
}

export function createNativeDraftState(document, bindings = []) {
  const draft = editableManifest(document)
  return { draft, bindings, selection: initialEditorSelection(draft), selected: null, savedFingerprint: draftFingerprint(draft, bindings), past: [], future: [], group: null, epoch: 0, validation: null, notice: '', noticeTone: 'neutral' }
}

function snapshot(state, label) { return { draft: state.draft, bindings: state.bindings, selection: state.selection, label } }

export function nativeDraftReducer(state, action) {
  switch (action.type) {
    case 'load': {
      const next = createNativeDraftState(action.manifest.document, action.manifest.section_bindings ?? [])
      return { ...next, selected: action.manifest, epoch: state.epoch + 1, notice: action.notice ?? '' }
    }
    case 'select':
      return { ...state, selection: validSelection({ ...state.selection, field: null, fieldSelectionId: null, ...action.selection }, state.draft), group: null }
    case 'edit': {
      const draft = typeof action.update === 'function' ? action.update(state.draft) : action.update
      const requestedBindings = typeof action.bindings === 'function' ? action.bindings(state.bindings) : action.bindings ?? state.bindings
      const bindings = effectiveSectionBindings(draft, requestedBindings)
      if (draftFingerprint(draft, bindings) === draftFingerprint(state.draft, state.bindings)) return state
      const past = action.group && state.group === action.group ? state.past : [...state.past, snapshot(state, action.label || 'edit')].slice(-NATIVE_HISTORY_LIMIT)
      return { ...state, draft, bindings, selection: validSelection(state.selection, draft), past, future: [], group: action.group ?? null, validation: null, notice: '', noticeTone: 'neutral' }
    }
    case 'finish-group': return state.group ? { ...state, group: null } : state
    case 'undo': {
      const previous = state.past.at(-1)
      if (!previous) return state
      return { ...state, draft: previous.draft, bindings: previous.bindings, selection: previous.selection, past: state.past.slice(0, -1), future: [...state.future, snapshot(state, previous.label)], group: null, validation: null, notice: `Undid ${previous.label}.`, noticeTone: 'neutral' }
    }
    case 'redo': {
      const next = state.future.at(-1)
      if (!next) return state
      return { ...state, draft: next.draft, bindings: next.bindings, selection: next.selection, past: [...state.past, snapshot(state, next.label)], future: state.future.slice(0, -1), group: null, validation: null, notice: `Redid ${next.label}.`, noticeTone: 'neutral' }
    }
    case 'saved': {
      if (action.request.epoch !== state.epoch) return state
      const unchangedSinceRequest = draftFingerprint(state.draft, state.bindings) === draftFingerprint(action.request.draft, action.request.bindings)
      const saved = editableManifest(action.manifest.document)
      const bindings = action.manifest.section_bindings ?? []
      return { ...state, selected: action.manifest, draft: unchangedSinceRequest ? saved : state.draft, bindings: unchangedSinceRequest ? bindings : state.bindings, savedFingerprint: draftFingerprint(saved, bindings), group: null, validation: null,
        notice: `Revision ${action.manifest.revision} saved${unchangedSinceRequest ? ' · checking…' : '. Newer edits are still unsaved.'}`, noticeTone: 'neutral' }
    }
    case 'validated': {
      if (state.epoch !== action.request.epoch || state.selected?.id !== action.request.id || draftFingerprint(state.draft, state.bindings) !== state.savedFingerprint) return state
      if (action.error) return { ...state, notice: `Revision ${action.request.revision} saved, but validation could not run.`, noticeTone: 'danger' }
      return { ...state, validation: action.result, notice: action.result.valid ? `Revision ${action.request.revision} saved and ready to publish.` : `Revision ${action.request.revision} saved · ${action.result.issues.length} issues need attention.`, noticeTone: action.result.valid ? 'success' : 'warning' }
    }
    case 'validation': return { ...state, validation: action.value }
    case 'selected': return { ...state, selected: typeof action.value === 'function' ? action.value(state.selected) : action.value }
    case 'notice': return { ...state, notice: action.value }
    case 'notice-tone': return { ...state, noticeTone: action.value }
    default: return state
  }
}
